import { Box, SxProps } from '@mui/material';
import { GridCellParams, GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';

import { getDisplayNameForRole, useAuth } from '@verantos-dev/core-auth';
import { UserRolesIdsEnum } from '@verantos-dev/core-enums';
import { useGetAllOrganizationsQuery } from '@verantos-dev/data-access-organizations';
import { ActionMenu, ActionMenuItemProps, StatusDisplay } from '@verantos-dev/shared-components';
import { clickableCellStyle, stringCellStyles } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';

import { DialogActionType, useUsersListDialogContext } from '../context/UsersListDialogContext';

const headerEntryStyles: SxProps = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: 'normal',
  padding: '16px 24px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#252A36',
};

export const useUsersListTable = () => {
  const intl = useIntl();
  const { dispatch } = useUsersListDialogContext();
  const { data: organizationsData } = useGetAllOrganizationsQuery('');
  const {
    user: { isAdmin },
  } = useAuth();

  // click handler for the users table - only interested in clicking on the name
  const onNameCellClick = (params: GridCellParams) => {
    const field = params.field;

    if (field === 'name') {
      dispatch({
        type: DialogActionType.OPEN_DIALOG,
        payload: 'displayUserInfo',
        data: params.row,
      });
    }
  };

  // the column headers for the user table
  const usersTableColumns: GridColDef[] = [
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.name' }),
      field: 'name',
      type: 'string',
      minWidth: 196,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const firstName = params.row.firstName;
        const lastName = params.row.lastName;
        const userId = params.row.userId;
        return (
          <Box
            data-testid={`users-name-${userId}`}
            sx={clickableCellStyle}
          >{`${firstName} ${lastName}`}</Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.status' }),
      field: 'userStatus',
      type: 'custom',
      minWidth: 130,
      maxWidth: 130,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const userId = params?.row.userId;
        return (
          <Box data-testid={`users-status-${userId}`} sx={stringCellStyles}>
            <StatusDisplay id={`users-status-${userId}`} status={params.value} />
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.email' }),
      field: 'email',
      type: 'string',
      minWidth: 306,
      flex: 2,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const userId = params?.row.userId;
        const email = params?.row.email || '';
        return (
          <Box data-testid={`users-email-${userId}`} sx={stringCellStyles}>
            {email}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.role' }),
      field: 'roles',
      type: 'string',
      minWidth: 196,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const userId = params?.row.userId;
        const roles = params.row.roles.map((role: UserRolesIdsEnum) =>
          getDisplayNameForRole(role, intl)
        );
        return (
          <Box data-testid={`users-roles-${userId}`} sx={stringCellStyles}>
            {roles.join(', ')}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.organization' }),
      field: 'organizationName',
      type: 'string',
      minWidth: 196,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const userId = params?.row.userId;
        const organization = organizationsData?.data.find(
          (org) => org.organizationId === params?.row.organizationId
        );
        const organizationName = organization?.displayName || params?.row.organizationName || '';
        return (
          <Box data-testid={`users-organization-${userId}`} sx={stringCellStyles}>
            {organizationName}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({ id: 'users.tableHeader.lastLogin' }),
      field: 'lastLogin',
      type: 'string',
      minWidth: 230,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        const userId = params?.row.userId;
        let lastLogin = params.row.lastLogin || '--';
        if (lastLogin !== '--') {
          lastLogin = formatDate(lastLogin, 'dateTimeFull');
        }

        return (
          <Box data-testid={`users-lastLogin-${userId}`} sx={stringCellStyles}>
            {lastLogin}
          </Box>
        );
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box data-testid={`users-list-header-${params.colDef.field}`} sx={headerEntryStyles}>
          {params.colDef.headerName}
        </Box>
      ),
    },
    {
      headerName: intl.formatMessage({
        id: 'common.grid.columnName.actions',
        defaultMessage: 'Actions',
      }),
      field: '',
      filterable: false,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',

      renderCell: (param) => {
        const actionMenu: ActionMenuItemProps[] = [
          {
            id: 'editUser',
            label: 'Edit user',
            disabled: !isAdmin,
            onClick: () => {
              dispatch({
                type: DialogActionType.OPEN_DIALOG,
                payload: 'editUser',
                data: param.row,
              });
            },
            icon: 'edit',
          },
        ];

        return <ActionMenu id={'userList-actionMenu'} menuItems={actionMenu} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    getDisplayNameForRole,
    onNameCellClick,
    usersTableColumns,
  };
};
